import type {
  CardDefinition,
  ContentRegistry,
  EncounterDefinition,
  EnemyDefinition,
  EventDefinition,
  RelicDefinition,
} from '../core/types';
import { cardDefinitions, rewardPool, starterDeck } from './cards';
import { encounterDefinitions, enemyDefinitions } from './enemies';
import { eventDefinitions } from './events';
import { relicDefinitions } from './relics';
import {
  cardDefinitionSchema,
  encounterDefinitionSchema,
  enemyDefinitionSchema,
  eventDefinitionSchema,
  relicDefinitionSchema,
} from './schemas';

function indexById<T extends { id: string }>(values: T[], label: string): Record<string, T> {
  const result: Record<string, T> = {};
  for (const value of values) {
    if (result[value.id]) throw new Error(`Duplicate ${label} id: ${value.id}`);
    result[value.id] = value;
  }
  return result;
}

function validateContent(): ContentRegistry {
  const cards = cardDefinitions.map((card) => cardDefinitionSchema.parse(card) as CardDefinition);
  const enemies = enemyDefinitions.map((enemy) => enemyDefinitionSchema.parse(enemy) as EnemyDefinition);
  const encounters = encounterDefinitions.map(
    (encounter) => encounterDefinitionSchema.parse(encounter) as EncounterDefinition,
  );
  const relics = relicDefinitions.map((relic) => relicDefinitionSchema.parse(relic) as RelicDefinition);
  const events = eventDefinitions.map((event) => eventDefinitionSchema.parse(event) as EventDefinition);

  const cardIndex = indexById(cards, 'card');
  const enemyIndex = indexById(enemies, 'enemy');
  const encounterIndex = indexById(encounters, 'encounter');
  const relicIndex = indexById(relics, 'relic');
  const eventIndex = indexById(events, 'event');

  for (const cardId of [...starterDeck, ...rewardPool]) {
    if (!cardIndex[cardId]) throw new Error(`Card pool references unknown card: ${cardId}`);
  }

  for (const encounter of encounters) {
    for (const enemyId of encounter.enemies) {
      if (!enemyIndex[enemyId]) {
        throw new Error(`Encounter ${encounter.id} references unknown enemy: ${enemyId}`);
      }
    }
  }

  for (const enemy of enemies) {
    const moves = [enemy.pattern, ...(enemy.phases?.map((phase) => phase.pattern) ?? [])].flat();
    for (const move of moves) {
      if ('statusCard' in move && move.statusCard) {
        const statusCard = cardIndex[move.statusCard.definitionId];
        if (!statusCard || statusCard.rarity !== 'status') {
          throw new Error(`Enemy ${enemy.id} references unknown status card: ${move.statusCard.definitionId}`);
        }
      }
    }
    let previousThreshold = enemy.maxHp + 1;
    for (const phase of enemy.phases ?? []) {
      if (phase.atHp >= previousThreshold || phase.atHp >= enemy.maxHp) {
        throw new Error(`Enemy ${enemy.id} has phases outside descending HP order.`);
      }
      previousThreshold = phase.atHp;
    }
  }

  for (const event of events) {
    for (const choice of event.choices) {
      for (const effect of choice.effects) {
        if (effect.kind === 'add_card' && !cardIndex[effect.cardDefinitionId]) {
          throw new Error(`Event ${event.id} references unknown card: ${effect.cardDefinitionId}`);
        }
        if (effect.kind === 'add_relic' && !relicIndex[effect.relicId]) {
          throw new Error(`Event ${event.id} references unknown relic: ${effect.relicId}`);
        }
      }
    }
  }

  return {
    cards: cardIndex,
    enemies: enemyIndex,
    encounters: encounterIndex,
    relics: relicIndex,
    events: eventIndex,
    starterDeck: [...starterDeck],
    rewardPool: [...rewardPool],
    version: 'ironvein-content-4',
  };
}

export const content = validateContent();
